import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StorefrontService } from "src/app/services/StorefrontService";
import { Basket, money, ProductType } from "./Basket";
import { clubTheme } from "./ClubTheme";
import { FanSession } from "./FanSession";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

type Holder = { name: string; email: string; dateOfBirth: string };

const blank = (): Holder => ({ dateOfBirth: "", email: "", name: "" });

const season = (start: string, end: string) =>
	`${new Date(start).getFullYear()}/${String(new Date(end).getFullYear()).slice(-2)}`;

/**
 * Buying a season ticket.
 *
 * Its own page rather than a card on the front, because it is the only thing
 * in the shop that asks who it is for. A season ticket lasts a year and goes
 * through a turnstile forty times; a club needs a name against each one, and
 * the only moment anybody will supply four names is while they are paying.
 *
 * The purchaser is prefilled as the first holder and can be changed. Assuming
 * they are all the same person is how a family of four ends up as four tickets
 * in one name.
 */
export function ShopSeasonTicket() {
	const { slug = "" } = useParams();
	const navigate = useNavigate();

	const [data, setData] = React.useState<any>(null);
	const [loading, setLoading] = React.useState(true);
	const [chosen, setChosen] = React.useState<any>(null);
	const [holders, setHolders] = React.useState<Holder[]>([blank()]);
	const [error, setError] = React.useState<string | null>(null);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.get(slug);
				if (live && res.ok) setData(await res.json());
			} catch {
				// The empty state covers it.
			} finally {
				if (live) setLoading(false);
			}
		})();
		return () => { live = false; };
	}, [slug]);

	// Prefill the buyer as the first holder once we know who they are.
	React.useEffect(() => {
		const profile = FanSession.profile();
		if (!profile) return;
		setHolders((current) => {
			if (current[0].name || current[0].email) return current;
			const next = [...current];
			next[0] = {
				dateOfBirth: "",
				email: profile.emailAddress || "",
				name: [profile.firstName, profile.lastName].filter(Boolean).join(" ")
			};
			return next;
		});
	}, []);

	const products: any[] = data?.seasonTickets || [];

	const setHolder = (index: number, patch: Partial<Holder>) =>
		setHolders((current) => current.map((h, i) => (i === index ? { ...h, ...patch } : h)));

	const add = () => {
		if (!chosen) return;

		// Every seat needs a name. A season ticket with nobody against it is a
		// job for the club office in August, which is when they have least time.
		const missing = holders.findIndex((h) => !h.name.trim());
		if (missing >= 0) {
			setError(`Who is season ticket ${missing + 1} for?`);
			return;
		}

		Basket.add(slug, {
			holders: holders.map((h) => ({
				dateOfBirth: h.dateOfBirth || null,
				email: h.email.trim() || null,
				name: h.name.trim()
			})),
			name: chosen.name,
			occasion: `${season(chosen.seasonStart, chosen.seasonEnd)} season ticket`,
			pricePence: chosen.pricePence,
			productId: chosen.id,
			productType: ProductType.SeasonTicket,
			quantity: holders.length,
			ticketTypeId: 0,
			when: chosen.seasonStart
		});
		navigate(`/club/${slug}/basket`);
	};

	if (loading) {
		return <div className="shop has-nav"><div className="shop-result"><span className="shop-spinner" /></div></div>;
	}

	if (!products.length) {
		return (
			<div className="shop has-nav" style={clubTheme(data?.club)}>
				<ShopHeader club={data?.club} slug={slug} />
				<div className="shop-result">
					<h2>No season tickets on sale</h2>
					<p>{data?.club?.name || "This club"} isn&rsquo;t selling season tickets at the moment.</p>
				</div>
				<ShopNav slug={slug} />
			</div>
		);
	}

	return (
		<div className="shop has-nav" style={clubTheme(data?.club)}>
			<ShopHeader club={data?.club} slug={slug} />
			<div className="shop-inner">
				<h2 className="shop-section-title">Season tickets</h2>

				{products.map((p) => (
					<button
						className={`shop-package${chosen?.id === p.id ? " chosen" : ""}`}
						key={p.id}
						onClick={() => setChosen(p)}
						type="button"
					>
						<div className="shop-package-head">
							<strong>{p.name}</strong>
							<span className="shop-price">{money(p.pricePence)}</span>
						</div>
						<div className="shop-package-meta">
							<span>{season(p.seasonStart, p.seasonEnd)} season</span>
							{p.isConcession && <span>Concession</span>}
						</div>
					</button>
				))}

				{chosen && <>
					<h2 className="shop-section-title">Who are they for?</h2>

					{holders.map((holder, index) => (
						<div className="shop-panel" key={index}>
							<div className="shop-holder-head">
								<strong>Season ticket {index + 1}</strong>
								{holders.length > 1 &&
									<button
										onClick={() => setHolders(holders.filter((_, i) => i !== index))}
										type="button"
									>
										Remove
									</button>}
							</div>

							<div className="shop-field">
								<label htmlFor={`h-name-${index}`}>Full name</label>
								<input id={`h-name-${index}`} onChange={(e) => setHolder(index, { name: e.target.value })} value={holder.name} />
							</div>

							<div className="shop-field">
								<label htmlFor={`h-email-${index}`}>Email (optional)</label>
								<input id={`h-email-${index}`} inputMode="email" onChange={(e) => setHolder(index, { email: e.target.value })} type="email" value={holder.email} />
							</div>

							{/* Only for a concession. Asking every adult for their
							    birthday to store for a year is worse than not asking. */}
							{chosen.isConcession &&
								<div className="shop-field">
									<label htmlFor={`h-dob-${index}`}>Date of birth</label>
									<input id={`h-dob-${index}`} onChange={(e) => setHolder(index, { dateOfBirth: e.target.value })} type="date" value={holder.dateOfBirth} />
								</div>}
						</div>
					))}

					<button className="shop-btn secondary" onClick={() => setHolders([...holders, blank()])} type="button">
						Add another
					</button>

					{error && <div className="shop-error" style={{ marginTop: 14 }}>{error}</div>}

					<div className="shop-sticky">
						<button className="shop-btn" onClick={add} type="button">
							Add {holders.length > 1 ? `${holders.length} season tickets` : "season ticket"} &middot;{" "}
							{money(chosen.pricePence * holders.length)}
						</button>
					</div>
				</>}
			</div>
			<ShopNav slug={slug} />
		</div>
	);
}
